import type { ColorScheme } from '@hover/blueprint/foundation';

import type { DesignerProduct } from 'src/modules/homeownerApp/productCatalogData';

export type RgbColor = `rgb(${number}, ${number}, ${number})`;
export type HexColor = `#${string}`;
export type Color = RgbColor | HexColor;

export type RgbTuple = [r: number, g: number, b: number];

export const isHex = (color: string): color is HexColor =>
  color.startsWith('#');
export const isRgb = (color: string): color is RgbColor =>
  color.startsWith('rgb(');
export const isColor = (color: string): color is Color =>
  isHex(color) || isRgb(color);

function assertColor(color: string, label?: string): asserts color is Color {
  const labelText = label ? ` for \`${label}\`` : '';

  if (!isColor(color)) {
    throw new Error(`Unsupported color format${labelText}: ${color}`);
  }
}

/**
 * Parse a hexidecimal color string into a numeric RGB tuple
 *
 * @param color color string in hexidecimal format
 *
 * @returns RGB tuple
 */
export const parseHex = (color: string): RgbTuple => {
  if (!isHex(color) || (color.length !== 4 && color.length !== 7))
    throw new Error(`Unsupported color format: ${color}`);

  const hex = color.substring(1);
  const expanded =
    hex.length === 3
      ? color
          .split('')
          .map(char => char + char)
          .join('')
      : hex;

  return [
    parseInt(expanded.substring(0, 2), 16),
    parseInt(expanded.substring(2, 4), 16),
    parseInt(expanded.substring(4, 6), 16),
  ];
};

/**
 * Parse a CSS RGB color string value into a numeric RGB tuple
 *
 * @param color color string in RGB format
 *
 * @returns RGB tuple
 */
export const parseRgb = (color: string): RgbTuple => {
  if (!isRgb(color)) throw new Error(`Unsupported color format: ${color}`);

  const matches = color.match(/\d+/g);

  if (matches?.length !== 3)
    throw new Error(`Unsupported color format: ${color}`);

  return matches.map(Number) as RgbTuple;
};

const RED_WEIGHT = 0.2126;
const GREEN_WEIGHT = 0.7152;
const BLUE_WEIGHT = 0.0722;

/**
 * Calculate the relative brightness of a color using the WCAG 2.0 formula
 *
 * @see {@link https://www.w3.org/WAI/GL/wiki/Relative_luminance}
 * @see {@link https://www.w3.org/TR/WCAG20/#relativeluminancedef}
 *
 * @param color color string in hexidecimal or RGB format
 *
 * @returns decimal brightness value
 */
export const brightness = (color: string): number => {
  assertColor(color);

  const rgb = isHex(color) ? parseHex(color) : parseRgb(color);
  const [r, g, b] = rgb.map(channel => channel / 255);

  const calculatedLuminance =
    (r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4) * RED_WEIGHT +
    (g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4) * GREEN_WEIGHT +
    (b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4) * BLUE_WEIGHT;

  return calculatedLuminance;
};

/**
 * Calculate the contrast ratio between two colors using the WCAG 2.0 formula
 *
 * @see {@link https://www.w3.org/TR/WCAG20/#contrast-ratiodef}
 *
 * @param background background color string in hexidecimal or RGB format
 * @param foreground foreground color string in hexidecimal or RGB format
 *
 * @returns contrast ratio
 */
export const getContrastRatio = (
  background: string,
  foreground: string,
): number => {
  assertColor(background, 'background');
  assertColor(foreground, 'foreground');

  const backgroundBrightness = brightness(background);
  const foregroundBrightness = brightness(foreground);

  const ratio =
    (Math.max(backgroundBrightness, foregroundBrightness) + 0.05) /
    (Math.min(backgroundBrightness, foregroundBrightness) + 0.05);

  return Math.round(ratio * 100) / 100;
};

export type ForegroundMode = Extract<ColorScheme, 'light' | 'dark'>;

/**
 * Given a background color and a light and dark foreground color, pick the
 * foreground color with the higher contrast ratio
 *
 * @param background background color string in hexidecimal or RGB format
 * @param light light foreground color string in hexidecimal or RGB format
 * @param dark dark foreground color string in hexidecimal or RGB format
 * @param adjust arbitrary multiplier to adjust the contrast ratio comparison to
 * "favor" lights or darks. This is useful in some cases when the ratio is very
 * close and we want to force a consistent foreground color in that case. A
 * value greater than 1 favors a light foreground and less than 1 favors a dark
 * foreground.
 *
 * @returns tuple with supplied light or dark with the higher contrast ratio and
 * the foreground mode as `'light' | 'dark'`
 */
export const pickForegroundColor = (
  background: string,
  light: string,
  dark: string,
  adjust: number = 1,
) => {
  const mode: ForegroundMode =
    getContrastRatio(background, dark) >
    getContrastRatio(background, light) * adjust
      ? 'dark'
      : 'light';

  return { color: mode === 'light' ? light : dark, mode } as const;
};

/**
 * Extracts RGB channels from a hex or rgb color string
 *
 * @param color Color in hex or rgb format
 *
 * @returns RGB tuple
 */
const extractColorChannels = (color: Color): RgbTuple =>
  isHex(color) ? parseHex(color) : parseRgb(color);

type HslTuple = [h: number, s: number, l: number];

/**
 * Converts RGB values to HSL color space
 * @param r Red channel (0-255)
 * @param g Green channel (0-255)
 * @param b Blue channel (0-255)
 *
 * @returns HSL tuple [hue (0-360), saturation (0-100), lightness (0-100)]
 */
const rgbToHsl = ([r, g, b]: RgbTuple): HslTuple => {
  const rNormalized = r / 255;
  const gNormalized = g / 255;
  const bNormalized = b / 255;

  const max = Math.max(rNormalized, gNormalized, bNormalized);
  const min = Math.min(rNormalized, gNormalized, bNormalized);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rNormalized) {
      h = 60 * (((gNormalized - bNormalized) / delta) % 6);
    } else if (max === gNormalized) {
      h = 60 * ((bNormalized - rNormalized) / delta + 2);
    } else {
      h = 60 * ((rNormalized - gNormalized) / delta + 4);
    }
  }
  if (h < 0) h += 360;

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return [h, s * 100, l * 100];
};

/**
 * Converts HSL values to RGB color space
 *
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 *
 * @returns RGB tuple (0-255)
 */
const hslToRgb = ([h, s, l]: HslTuple): RgbTuple => {
  const sNormalized = s / 100;
  const lNormalized = l / 100;

  const c = (1 - Math.abs(2 * lNormalized - 1)) * sNormalized;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNormalized - c / 2;

  let [r, g, b] = [0, 0, 0];

  if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
  else if (h >= 60 && h < 120) [r, g, b] = [x, c, 0];
  else if (h >= 120 && h < 180) [r, g, b] = [0, c, x];
  else if (h >= 180 && h < 240) [r, g, b] = [0, x, c];
  else if (h >= 240 && h < 300) [r, g, b] = [x, 0, c];
  else if (h >= 300 && h < 360) [r, g, b] = [c, 0, x];

  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
};

/**
 * Darkens and saturates a color
 * @param color The color to adjust (hex or rgb)
 * @param darkenPercent The percentage to darken by (0-100)
 * @param saturatePercent The percentage to increase saturation by (0-100)
 * @returns Adjusted hex color
 */
const adjustColor = (
  color: Color,
  darkenPercent: number,
  saturatePercent: number = darkenPercent / 20,
): HexColor => {
  const rgb = extractColorChannels(color);
  const [h, s, l] = rgbToHsl(rgb);

  // Increase saturation and decrease lightness
  const newS = Math.min(100, s + saturatePercent);
  const newL = Math.max(0, l * (1 - darkenPercent / 100));

  const newRgb = hslToRgb([h, newS, newL]);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${newRgb.map(toHex).join('')}`;
};

/**
 * Ensures a background color meets a target contrast ratio with a given foreground color
 * by progressively darkening and saturating the background until it meets the minimum ratio
 *
 * @param background Background color in hex or rgb format
 * @param foreground Foreground color in hex or rgb format
 * @param options - Optional configuration:
 *   @property {number} [ratio=4.5] - Target contrast ratio (defaults to WCAG AA standard 4.5:1).
 *   @property {number} [darkenStep=5] - The amount by which to darken the background on each attempt.
 *   @property {number} [maxAttempts=20] - Maximum number of attempts to adjust the background.
 * @returns Adjusted background color that meets contrast requirements
 */
export const ensureBackgroundContrast = (
  background: Color,
  foreground: Color,
  options?: {
    ratio?: number;
    darkenStep?: number;
    maxAttempts?: number;
  },
): HexColor => {
  const { ratio = 4.5, darkenStep = 5, maxAttempts = 20 } = options || {};

  const adjustBackground = (current: Color, attempt: number = 0): HexColor => {
    const currentRatio = getContrastRatio(current, foreground);

    if (currentRatio >= ratio || attempt >= maxAttempts) {
      return isHex(current) ? current : adjustColor(current, 0);
    }

    return adjustBackground(adjustColor(current, darkenStep), attempt + 1);
  };

  return adjustBackground(background);
};

export const productBrightnessWithDefault = (product: DesignerProduct) =>
  product.color?.hex ? brightness(product.color.hex) : 0;
